import {
  castToBoolean,
  createError,
  getUserFacingError,
} from '../../../lib/util';
import { redactSensitiveValues } from '../../../lib/redact';

class RecapResponseController {
  constructor(
    $q,
    $scope,
    $log,
    $timeout,
    $anchorScroll,
    $window,
    geolocation,
    RecapResponseService,
    LogicFilterService,
    TaskQueueService,
    StaffUserService,
    DataSyncService,
    DateTimeService,
    LogService,
    AnalyticsService,
    APIService,
    $location,
    moment,
    MerchandisingRecapScopeService
  ) {
    "ngInject";
    this.$q                   = $q;
    this.$scope               = $scope;
    this.$log                 = $log;
    this.$timeout             = $timeout;
    this.$anchorScroll        = $anchorScroll;
    this.$window              = $window;
    this.geolocation          = geolocation;
    this.RecapResponseService = RecapResponseService;
    this.LogicFilterService   = LogicFilterService;
    this.TaskQueueService     = TaskQueueService;
    this.StaffUserService     = StaffUserService;
    this.DataSyncService      = DataSyncService;
    this.DateTimeService      = DateTimeService;
    this.LogService           = LogService;
    this.AnalyticsService     = AnalyticsService;
    this.APIService           = APIService;
    this.$location            = $location;
    this.moment               = moment;
    this.MerchandisingRecapScopeService = MerchandisingRecapScopeService;

    this._loadingRecapResponse  = false;
    this._partialResponseEnqueued = false;
    this._savingRecapResponse   = false;
    this._savingPartialResponse   = false;
    this._recapResponseSaved    = false;
    this._recapResponseEnqueued = false;
    this._recapResponsePreviewCompleted = false;
    this._isOnboarding          = false;
    this._staffStatus           = null;
    this.errors                 = [];
    this.messages               = [];
    this.flashMessages          = [];

    this._logicFiltersData = {};
    this._isFilterDataLoaded = false;

    this.coordinates = {
      startCoordinates: {
        lat: null,
        lon: null,
      },
      endCoordinates: {
        lat: null,
        lon: null,
      }
    }

    this._constructed = true;
  }

  /**
   * Controller lifecycle
   */

  $onInit() {
    const promises = [];

    this.coordinates.startCoordinates = { lat: null, lon: null }

    this.geolocation.getLocation().then((data) => {
      this.coordinates.startCoordinates.lat = data?.coords?.latitude
      this.coordinates.startCoordinates.lon = data?.coords?.longitude
    })
      .catch(() => {
        this.LogService.error("Error getting location for the start of the recap");
      });

    // Named fields, not `this`: the instance carries bearer credentials.
    this.LogService.log('response.controller $onInit', {
      recapDefinitionId: this.recapDefinitionId,
      recapResponseSetId: this.recapResponseSetId,
      recapType: this.recapType,
      eventId: this.eventId,
      staffingUsersId: this.staffingUsersId,
      isOnboarding: this.isOnboarding,
    });

    this.$anchorScroll('header');

    // Log that a token was found, never the token: these go straight to the console.
    if (this.eventsRecapsExternalLinksToken) {
      // Set login credentials with link token
      this.LogService.debug(`ResponseController.$onInit() setting external link credentials`);
      this.APIService.setRecapExternalLinkCredentials(this.eventsRecapsExternalLinksToken);
    } else if (this.applicationId) {
      this.LogService.debug(`ResponseController.$onInit() setting onboarding application credentials`);
      this.APIService.setApplicationCredentials(this.applicationId);
    } else {
      this.LogService.debug(`ResponseController.$onInit() no token!`);
    }

    // Load recap response set if ID present
    if (this.recapResponse) {
      this._initializeExistingRecapResponse();
      this.$log.info("Recap response already loaded:", this.recapResponse);
    } else if (this.recapResponseSetId) {
      promises.push(this._loadRecapResponseSet(this.recapResponseSetId));
    } else if (this.recapDefinitionId) {
      const eventId = this.eventId || null;
      const staffingUsersId = this.staffingUsersId || null;
      const usersId = this.usersId || null;
      promises.push(this._createRecapResponseForRecapDefinitionId(this.recapDefinitionId, eventId, staffingUsersId, usersId));
    } else {
      this.$log.error("No recap definition or recap response provided", {
        recapDefinitionId: this.recapDefinitionId,
        recapResponseSetId: this.recapResponseSetId,
        recapType: this.recapType,
        eventId: this.eventId,
        staffingUsersId: this.staffingUsersId,
        isOnboarding: this.isOnboarding,
      });
    }

    // Load logic filter data
    promises.push(
      this.LogicFilterService.getFilterData({
        'type': this.recapType,
        'data': {
          'events_id': this.eventId,
          'venue_id': this.venuesId,
          'staffing_users_id': this.staffingUsersId,
        },
      })
        .then(data => {
          this._logicFiltersData = data;
        })
        .finally(() => {
          this._isFilterDataLoaded = true;
        })
    );

    const jumpToPageIndex = visiblePageIndex => {
      const visiblePages = this.recapResponse && this.recapResponse.visiblePages || [];
      if (visiblePageIndex >= visiblePages.length || visiblePageIndex < 0) {
        this.LogService.error(`Page ${visiblePageIndex} is out of bounds; have ${visiblePages.length} pages:`, visiblePages);
        return false;
      }

      const requestedPage = visiblePages[visiblePageIndex];
      const currentPage = this.page;
      const currentPageResponse = this.pageResponse;

      if (requestedPage === currentPage) {
        return false;
      }

      // Validate & Mark current page as viewed
      if (currentPageResponse) {
        currentPageResponse.view();
        currentPageResponse.validate();
      } else {
        this.LogService.warn(`ResponseController jumpToPageIndex(${visiblePageIndex}) no current pageResponse to mark as viewed`);
      }

      this.page = requestedPage;
      this.scrollToTop();
      return true;
    };

    this.$scope.$on('nextPage', (event, data) => {
      let pageResponse = data.pageResponse;
      let visiblePages = this.recapResponse.visiblePages;
      let idx = visiblePages.indexOf(pageResponse.page);
      return jumpToPageIndex(idx + 1);
    });

    this.$scope.$on('previousPage', (event, data) => {
      let pageResponse = data.pageResponse;
      let visiblePages = this.recapResponse.visiblePages;
      let idx = visiblePages.indexOf(pageResponse.page);
      return jumpToPageIndex(idx - 1);
    });

    this.$scope.$on('jumpToPage', (event, data) => {
      const pageIndex = data.pageIndex;
      return jumpToPageIndex(pageIndex);
    });

    this.$scope.$on('exit', (event, data) => {
    });

    this.$scope.$on('updateDisplayLogic', (event, fieldResponse) => {
      // Handle updating of display logic
      this.recapResponse.updateDisplayLogic([fieldResponse]);
      this.recapResponse.updateLogicModifiers([fieldResponse]);
      this.$scope.$broadcast('displayLogicUpdated');
    });

    const stopWatching = this.$scope.$watch(() => this._isFilterDataLoaded, watcherFn);

    const watcherThis = this;
    function watcherFn() {
      if (watcherThis._isFilterDataLoaded) {
        if (watcherThis.recapResponse) {
          watcherThis.recapResponse.logicFiltersData = watcherThis._logicFiltersData;
          watcherThis.recapResponse.updateDisplayLogic();
          watcherThis.$scope.$broadcast('displayLogicUpdated');
          stopWatching();
        } else {
          watcherThis.$timeout(watcherFn, 20);
        }
      }
    }
    return this.$q.all(promises);
  }

  onRecapResponsePageFinish(data) {
    // Prevent duplicate submissions
    if (this._savingRecapResponse) {
      this.LogService.warn("RecapResponseController.onRecapResponsePageFinish() save already in progress, ignoring");
      return this.$q.when(null);
    }

    const pageResponse = data && data.pageResponse || null;

    // Mark all pages as viewed & touched
    this.recapResponse.viewAllPages();
    this.recapResponse.touchAllPages();

    let allErrors = this.recapResponse.validate();
    if (allErrors.length == 0) {
      let savePromise;
      if (this.allowSave) {
        return this._saveRecapResponseSet()
          .then(recapResponse => {
            if (recapResponse && this.onFinish) {
              this.onFinish({recapResponse});
            } else if (!recapResponse) {
              this.LogService.error("Recap response not defined after attempted save");
            }
          })
      } else {
        return this._showPreviewCompletedMessage();
      }
    } else {
      const errorsByPage = this.recapResponse.validateByPage();
      let firstPageWithErrorsIdx;
      for (let idx in errorsByPage) {
        if (errorsByPage[idx].length > 0) {
          firstPageWithErrorsIdx = idx;
          break;
        }
      }

      // Skip to page [idx]
      this.LogService.warn("Found errors; skipping to page", firstPageWithErrorsIdx);
      this.page = this.recapResponse.visiblePages[firstPageWithErrorsIdx];

      return this.$timeout(() => {
        this.$scope.$broadcast('scrollToFirstError');
      }, 10);
    }
  }

  onRecapResponsePageExit() {
    this.LogService.info("Got exit signal; returning");
    return this.exit();
  }

  /**
   * Public properties
   */

  get loading() {
    return this._loadingRecapResponse || this._savingRecapResponse;
  }

  get recapResponse() {
    return this._recapResponse;
  }

  set recapResponse(recapResponse) {
    this._recapResponse = recapResponse;
    if (!this._constructed) return;
    this._initializeRecapResponse();
  }

  set page(page) {
    const visiblePages = this.recapResponse && this.recapResponse.visiblePages || [];
    const pageIndex = visiblePages.indexOf(page);
    this._page = page;
    this.visiblePageIndex = pageIndex;
  }

  get page() {
    return this._page;
  }

  set visiblePageIndex(idx) {
    this._visiblePageIndex = idx;
  }

  get visiblePageIndex() {
    return this._visiblePageIndex;
  }

  get pageResponse() {
    let pageResponse = null;
    if (this.recapResponse && this.page) {
      pageResponse = this.recapResponse.getPageResponse(this.page);
    }
    return pageResponse;
  }

  get event() {
    return this.recapResponse && this.recapResponse.event;
  }

  get eventStartTime() {
    if (this.event) {
      return this.event.start_timestamp;
    } else {
      return null;
    }
  }

  get eventEndTime() {
    if (this.event) {
      return this.event.end_timestamp;
    } else {
      return null;
    }
  }

  get recapResponseSaved() {
    return this._recapResponseSaved;
  }

  get showRecapResponseSavedMessage() {
    return this.recapResponseSaved;
  }

  get partialResponseEnqueued() {
    return this._partialResponseEnqueued;
  }

  get recapResponseEnqueued() {
    return this._recapResponseEnqueued;
  }

  get showRecapResponseEnqueuedMessage() {
    return this.recapResponseEnqueued;
  }

  get recapResponsePreviewCompleted() {
    return this._recapResponsePreviewCompleted;
  }

  get showRecapResponsePreviewCompletedMessage() {
    return this.recapResponsePreviewCompleted;
  }

  get showRecapResponse() {
    return this.recapResponse
      && !this.showRecapResponseSavedMessage
      && !this.showRecapResponseEnqueuedMessage
      && !this.showRecapResponsePreviewCompletedMessage
      && !this.loading;
  }

  set allowExit(allow) {
    this._allowExit = allow;
  }

  get allowExit() {
    return this._allowExit;
  }

  set allowSave(allow) {
    this._allowSave = allow;
  }

  get allowSave() {
    if (this._allowSave === undefined || this._allowSave === null) {
      return true;
    } else {
      return this._allowSave;
    }
  }

  get showExitButton() {
    return this.allowExit;
  }

  get showExitButtonAfterRecap() {
    return this.allowExit && !this.showSubmitClaimsAfterRecap && !this.showCheckoutClaimsAfterRecap;
  }

  get showCloseMessageAfterRecap() {
    return !this.allowExit;
  }

  get showCheckoutClaimsAfterRecap() {
    if (!this.allowExit) {
      return false;
    }

    const position = this.recapResponse && this.recapResponse.position;
    const event = position && position.event;

    const recapCheckoutClaimEnabled = event && castToBoolean(event.prompt_recap_checkout_claim);
    if (!recapCheckoutClaimEnabled) {
      return false;
    }

    const staffUser = this.StaffUserService.currentUser || null;
    if (!staffUser) {
      return false;
    }

    return !!(position.allow_event_checkin && !position.staffUserCanEditClaims(staffUser))
  }

  get showSubmitClaimsAfterRecap() {
    if (!this.allowExit) {
      return false;
    }

    const position = this.recapResponse && this.recapResponse.position;
    const event = position && position.event;
    const recapClaimLinkEnabled = event && castToBoolean(event.prompt_recap_claim);
    if (!recapClaimLinkEnabled) {
      return false;
    }

    const staffUser = this.StaffUserService.currentUser || null;
    if (!staffUser) {
      return false;
    }

    // Determine whether "Submit Claims" dashboard button would be enabled
    return !!(position.canSubmitClaims && !position.staffUserCanEditClaims(staffUser));
  }

  set showLanguageSelector(show) {
    this._showLanguageSelector = show;
  }

  get showLanguageSelector() {
    return (this._showLanguageSelector !== undefined)
      ? !!this._showLanguageSelector
      : false;
  }

  get getFlashMessages() {
    return this.flashMessages;
  }

  get isOnboarding() {
    return castToBoolean(this._isOnboarding);
  }

  set isOnboarding(isOnboarding) {
    this._isOnboarding = isOnboarding;
  }

  get staffStatus() {
    return this._staffStatus;
  }

  set staffStatus(staffStatus) {
    this._staffStatus = staffStatus;
  }

  emitStatus() {
    console.log(this.staffStatus, this._staffStatus);
  }

  setFlashMessages(flashMessages) {
    this.flashMessages.push(...flashMessages);
  }

  /**
   * Public methods
   */
  scrollToTop() {
    this.$anchorScroll('recap_response_' + this.recapResponse.id);
  }

  closeWindow() {
    this.$window.close();
  }

  showResponse() {
    this._recapResponseSaved = false
    this.recapResponse.started_at = new Date()
    this.geolocation.getLocation().then((data) => {
      this.recapResponse.coords.startCoordinates.lat = data?.coords?.latitude
      this.recapResponse.coords.startCoordinates.lon = data?.coords?.longitude
    })
      .catch(() => {
        this.LogService.error("Error getting location for the start of the recap")
      })
  }

  exit() {
    if (this.onExit) {
      return this.onExit({ recapResponse: this.recapResponse });
    }
    return false;
  }

  submitClaims(checkout = false) {
    if (this.onSubmitClaims) {
      return this.$q.when(() => {
        if (this.DataSyncService.isOnline) {
          this.LogService.info("Submitting claims after submitting recap");
          return this.DataSyncService.sync();
        } else {
          this.LogService.info("Not submitting claims after submitting recap because we are offline");
          return null;
        }
      })
        .then(() => {
          if (checkout) {
            const position = this.recapResponse && this.recapResponse.position;
            if (position) {
              return this.checkOutPosition(position)
                .then(() => this.onSubmitClaims({ recapResponse: this.recapResponse }))
                .catch(err => {
                  this.LogService.error("Error during checkout while submitting claims:", err);
                  return this.onSubmitClaims({ recapResponse: this.recapResponse });
                });
            }
          }
          return this.onSubmitClaims({ recapResponse: this.recapResponse })
        });
    }
  }

  /**
   * Private methods
   */

  _pushError(error) {
    this.errors.push(error);
  }

  _clearErrors() {
    this.errors = [];
  }

  _initializeExistingRecapResponse() {
    if (!this.recapResponse) {
      this.LogService.warn("Recap response was not present");
      return;
    }
    this._initializeRecapResponse();
    if (this.recapResponse.hasResponses) {
      this._validateAndMarkRecapResponseAsViewed(this.recapResponse);
    }
  }

  _initializeNewRecapResponse() {
    if (!this.recapResponse) {
      this.LogService.warn("Recap response was not present");
      return;
    }
    this._initializeRecapResponse();
  }

  _initializeRecapResponse() {
    if (!this.recapResponse) {
      this.LogService.warn("Recap response was not present");
      return;
    }

    // Set external link view ID
    this.recapResponse.events_recaps_external_links_views_id = this.eventsRecapsExternalLinksViewsId;
    this.LogService.debug(`Set recapResponse.events_recaps_external_links_views_id to ${this.recapResponse.events_recaps_external_links_views_id}`);

    // Overlay merchandising brand/label scope onto product forms before
    // updateDisplayLogic materializes anything downstream. When merchandising
    // is disabled or there's no eventId, the overlay resolves to a no-op and
    // legacy products-table forms render unchanged.
    return this._ensureMerchandisingScope()
      .then(scope => {
        this.MerchandisingRecapScopeService.applyOverlay(this.recapResponse, scope);

        this.recapResponse.updateDisplayLogic();
        this.recapResponse.updateLogicModifiers();

        if (this.recapResponse.visiblePages.length) {
          this.page = this.recapResponse.visiblePages[0];
        } else {
          this.page = null;
        }
        this.LogService.debug("Loaded recap response:", this.recapResponse);

        // Send analytics!
        const eventName = 'INITIALIZE_RECAP_RESPONSE_TOOL';
        return this.$q.when(this.recapResponse)
          .then(recapResponse => this.$q.when(this.AnalyticsService.trackEvent(
            eventName,
            this.RecapResponseService.getFullDiagnosticInfoForRecapResponse(recapResponse)
          )))
          .catch(err => {
            this.LogService.error(`Swallowing error sending ${eventName}:`, err);
          })
          .then(() => {
            this.recapResponse.started_at = new Date()
            this.recapResponse.coords.startCoordinates = this.coordinates.startCoordinates
          })
          .finally(() => this.recapResponse);
      });
  }

  /**
   * Cache and return the merchandising scope promise for the current event.
   * Returns a resolved no-op when there's no eventId so the rest of the recap
   * load doesn't have to special-case the legacy flow.
   */
  _ensureMerchandisingScope() {
    if (!this._merchandisingScopePromise) {
      // The staff app passes a pre-loaded recapResponse without the event-id
      // binding; fall back to the recap response's eventId so the scope
      // overlay and the activity-types form filter both run on that path.
      const eventId = this.eventId
        || (this.recapResponse && this.recapResponse.eventId)
        || null;
      if (!eventId) {
        this._merchandisingScopePromise = this.$q.when({
          enabled: false,
          allow_additional: false,
          effective: [],
          additional: [],
          merchandising_enabled: false,
          event_merchandising_activities_ids: [],
        });
      } else {
        this._merchandisingScopePromise = this.$q.when(
          this.MerchandisingRecapScopeService.getForEvent(eventId)
        );
      }
    }
    return this._merchandisingScopePromise;
  }

  _createRecapResponseForRecapDefinitionId(recapDefinitionId, eventId = null, staffingUsersId = null, usersId = null) {
    this._loadingRecapResponse = true;
    this._recapResponse = null;
    this._clearErrors();
    const previewLocationVariableIds = this.previewLocationVariableIdsCommaSeparated && this.previewLocationVariableIdsCommaSeparated.split(',') || [];
    let data = {
      events_recaps_id: recapDefinitionId,
      previewLocationVariableIds: previewLocationVariableIds,
      events_id: eventId,
      staffing_users_id: staffingUsersId,
      users_id: usersId,
      role_variable_id: this.previewRoleVariableId
    };
    this.LogService.debug(`ResponseController._createRecapResponseForRecapDefinitionId() creating with data:`, data);
    this.RecapResponseService.create(data)
      .then(recapResponse => {
        this.recapResponse = recapResponse;
        this._initializeNewRecapResponse();
      })
      .catch(error => {
        // LogService does not scrub the console, and an $http rejection carries basic auth.
        this.LogService.error("Error creating recap response:", redactSensitiveValues(error));
        this._pushError(getUserFacingError(error) || {
          key: 'ERROR_CREATING_RECAP_RESPONSE',
          message: 'Error creating recap response',
        });
      })
      .finally(() => {
        this._loadingRecapResponse = false;
      });
  }

  _loadRecapResponseSet(id) {
    this._recapResponse = null;
    this._loadingRecapResponse = true;
    this._clearErrors();
    return this.RecapResponseService.getById(id)
      .then(recapResponse => {
        this.recapResponse = recapResponse;
        this._initializeExistingRecapResponse();
      })
      .catch(error => {
        // LogService does not scrub the console, and an $http rejection carries basic auth.
        this.LogService.error("Error loading recap response:", redactSensitiveValues(error));
        this._pushError(getUserFacingError(error) || {
          key: 'ERROR_LOADING_RECAP_RESPONSE',
          message: 'Error loading recap response',
        });
      })
      .finally(() => {
        this._loadingRecapResponse = false;
      })
  }

  _validateAndMarkRecapResponseAsViewed(recapResponse) {
    recapResponse.validate();
    recapResponse.touchAllPages();
    recapResponse.viewAllPages();
  }

  onSavePartialResponse() {
    this.LogService.debug("RecapResponseController.onSavePartialResponse() arguments:", arguments);
    return this._savePartialResponseSet();
  }

  async _savePartialResponseSet() {
    if (!this.allowSave) {
      this.LogService.warn("Not allowing a partial save to happen as allowSave is false");
      return this.$q.reject(createError({
        error: 'SaveNotAllowed',
        message: 'Not allowing partial save',
      }));
    }

    const isOnboardingRecap = this.isOnboarding || this.recapResponse?.recap_definition?.type === 'staff';
    const hasNoKnownUser = !!this.eventsRecapsExternalLinksToken;
    if (isOnboardingRecap || hasNoKnownUser) {
      this.LogService.debug(`Skipping partial save`);
      return null;
    }

    this._savingPartialResponse = true;

    this.LogService.info("Saving partial response:", this.recapResponse);

    const gpsPromise = new Promise((resolve) => {
      let resolved = false;
      const doResolve = () => {
        if (!resolved) {
          resolved = true;
          if (!this.recapResponse.coords) {
            this.recapResponse.coords = {};
          }
          this.recapResponse.coords.endCoordinates = this.coordinates.endCoordinates;
          resolve({
            end_lat: this.coordinates.endCoordinates.lat,
            end_lon: this.coordinates.endCoordinates.lon,
          });
        }
      };

      setTimeout(doResolve, 5000);

      this.geolocation.getLocation()
        .then((data) => {
          this.coordinates.endCoordinates.lat = data?.coords?.latitude;
          this.coordinates.endCoordinates.lon = data?.coords?.longitude;
        })
        .catch(() => {
          this.LogService.error("Error getting location for the end of the recap");
        })
        .finally(doResolve);
    });

    let promise;
    let task = this.TaskQueueService.createTask('PartialRecapResponseSaveTask');
    task.setRecapResponse(this.recapResponse);
    task.gpsPromise = gpsPromise;

    if (task.canExecute) {
      promise = task.execute()
        .then(recapResponse => {
          this.LogService.info('_savePartialResponseSet() Success! Recap response:', recapResponse);
          return recapResponse;
        })
        .catch(error => {
          this.LogService.error("Error saving recap response:", error);
          if (Array.isArray(error)) {
            error.forEach(err => this._pushError(err));
          } else {
            this._pushError(error);
          }
        });
    } else {
      await gpsPromise;
      task.recapResponseData.end_lat = this.recapResponse.coords?.endCoordinates?.lat;
      task.recapResponseData.end_lon = this.recapResponse.coords?.endCoordinates?.lon;

      this.LogService.log('Enqueued partial recap response save task');
      this._partialResponseEnqueued = true;

      promise = this.TaskQueueService.enqueue(task)
        .then(() => {
          if (this.recapResponsePosition) {
            this.recapResponsePosition.enqueuedPartialResponse = this.recapResponse;
          }
          this._partialResponseEnqueued = true;
        });
    }

    return promise.finally(() => {
      this._savingPartialResponse = false;
    });
  }

  async _saveRecapResponseSet() {
    this._savingRecapResponse = true;
    this._recapResponseSaved = false;
    this._recapResponseEnqueued = false;

    this._clearErrors()
    this.LogService.info("Saving recap response:", this.recapResponse);

    if (this.eventsRecapsExternalLinksToken) {
      this.APIService.setRecapExternalLinkCredentials(this.eventsRecapsExternalLinksToken);
    }

    const gpsPromise = new Promise((resolve) => {
      let resolved = false;
      const doResolve = () => {
        if (!resolved) {
          resolved = true;
          if (!this.recapResponse.coords) {
            this.recapResponse.coords = {};
          }
          this.recapResponse.coords.endCoordinates = this.coordinates.endCoordinates;
          resolve({
            end_lat: this.coordinates.endCoordinates.lat,
            end_lon: this.coordinates.endCoordinates.lon,
          });
        }
      };

      setTimeout(doResolve, 5000);

      this.geolocation.getLocation()
        .then((data) => {
          this.coordinates.endCoordinates.lat = data.coords.latitude;
          this.coordinates.endCoordinates.lon = data.coords.longitude;
        })
        .catch(() => {
          this.LogService.error("Error getting location for the end of the recap");
        })
        .finally(doResolve);
    });

    let promise;
    let task = this.TaskQueueService.createTask('RecapResponseSaveTask');
    task.setRecapResponse(this.recapResponse);
    task.gpsPromise = gpsPromise;

    if (task.canExecute) {
      const savedPosition = this.recapResponse.position;
      promise = task.execute()
        .then(recapResponse => {
          if (recapResponse.flashMessages && recapResponse.flashMessages.length) {
            this.setFlashMessages(recapResponse.flashMessages);
            delete recapResponse.flashMessages;
          }
          this.LogService.info('_saveRecapResponseSet() Success! Recap response:', recapResponse);
          this._recapResponseSaved = true;
          recapResponse.position = savedPosition;
          this._validateAndMarkRecapResponseAsViewed(recapResponse);
          this.recapResponse = recapResponse;
          return recapResponse;
        })
        .catch(error => {
          this.LogService.error("Error saving recap response:", error);
          if (Array.isArray(error)) {
            error.forEach(err => this._pushError(err));
          } else {
            this._pushError(error);
          }
        });
    }
    else {
      await gpsPromise;
      task.recapResponseData.end_lat = this.recapResponse.coords?.endCoordinates?.lat;
      task.recapResponseData.end_lon = this.recapResponse.coords?.endCoordinates?.lon;

      this.LogService.log("Enqueued recap response save task");
      promise = this.TaskQueueService.enqueue(task)
        .then(() => {
          this.recapResponse.enqueued = true;
          if (this.recapResponsePosition) {
            this.recapResponsePosition.enqueuedRecapResponse = this.recapResponse;
          }
          this._recapResponseEnqueued = true;
          return this.recapResponse;
        });
    }

    return promise
      .finally(() => {
        this._savingRecapResponse = false;
      });
  }

  _showPreviewCompletedMessage() {
    this._recapResponsePreviewCompleted = true;
    return this.$q.when(null);
  }

  get savingPartialResponse() {
    return this._savingPartialResponse;
  }

  get savingRecapResponse() {
    return this._savingRecapResponse;
  }

  //NOTE: This is duplicated from positionDisplayBox.
  checkOutPosition(position) {
    if (!position) return this.$q.reject(new Error('No position provided'));

    const checkInPromise = this.geolocation.getLocation()
      .catch(() => {
        this.LogService.error(`Geolocation unavailable during checkout from recap`);
        return {
          coords: {
            latitude: 0,
            longitude: 0,
          }
        };
      });

    return checkInPromise.then(locationData => {
      const task = this.TaskQueueService.createTask('CheckinCheckoutTask');
      task.lat = locationData.coords.latitude;
      task.lon = locationData.coords.longitude;

      const timestamp = this.moment().format();

      task.staffUserId = this.StaffUserService.currentUser.id;
      task.timestamp = timestamp;
      task.direction = 'out';
      task.eventId = position.real_events_id;

      // Creating a local checkin/out. Typically, these are returned on sync.
      // This is needed because we need the actual checkin data to populate
      // in the claim editor.
      const localCheckin = {
        timestamp,
        type: 'out',
        events_id: position.real_events_id,
      };

      if (task.canExecute) {
        return task.execute()
          .then(result => {
            position.toggleCheckinStatus();
            position.pushLocalCheckin(localCheckin);
            this.LogService.info(`Successfully checked out from event ${position.real_events_id} during recap`);
            return result;
          })
          .catch(err => {
            this.LogService.error(`Error executing checkout task from recap (event=${position.real_events_id}):`, err);
            return this.$q.reject(err);
          });
      } else {
        // Enqueue task for later execution
        return this.TaskQueueService.enqueue(task)
          .then(() => {
            this.LogService.debug(`Checkout task enqueued from recap; toggling status (event=${position.real_events_id})`);
            position.toggleCheckinStatus();
            position.pushLocalCheckin(localCheckin);
          });
      }
    });
  }

}

export default RecapResponseController;
